import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Tag, Package, ShoppingBag, Plus, Minus, ShoppingCart, X, Truck, HandHelping, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";

interface Banner { id: string; title: string | null; subtitle: string | null; image_url: string; link_url: string | null; }

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  stock: number;
  image_url: string | null;
  is_promotional: boolean;
  promotional_price: number;
  sku?: string | null;
  category?: string | null;
  images?: string[] | null;
  videos?: string[] | null;
}

interface CartItem {
  product: Product;
  quantity: number;
}

const Store = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [shopEnabled, setShopEnabled] = useState<boolean | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("mentesa_cart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [detail, setDetail] = useState<Product | null>(null);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Checkout form
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"pickup" | "delivery" | "massage_pickup">("pickup");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [notes, setNotes] = useState("");

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-open checkout if returning from auth
  useEffect(() => {
    if (user && searchParams.get("checkout") === "1" && cart.length > 0) {
      setCheckoutOpen(true);
      // Clean URL params
      navigate("/loja", { replace: true });
    }
  }, [user]);

  useEffect(() => {
    const init = async () => {
      const { data: setting } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "shop_enabled")
        .maybeSingle();

      if (setting) {
        const val = setting.value;
        const enabled = val === true || val === "true" || val === '"true"' || JSON.stringify(val) === "true";
        setShopEnabled(enabled);
        if (!enabled) { setLoading(false); return; }
      } else {
        setShopEnabled(false);
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from("products_public")
        .select("id, name, description, price, stock, image_url, is_promotional, promotional_price, sku, category, images, videos")
        .eq("is_active", true)
        .order("is_promotional", { ascending: false });
      setProducts((data as unknown as Product[]) || []);
      const { data: b } = await (supabase as any).from("store_banners").select("id, title, subtitle, image_url, link_url").eq("is_active", true).order("sort_order");
      setBanners(b || []);
      setLoading(false);
    };
    init();
  }, []);

  // Pre-fill user info
  useEffect(() => {
    if (user) {
      setClientEmail(user.email || "");
      setClientName(user.user_metadata?.full_name || "");
    }
  }, [user]);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem("mentesa_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))] as string[];
  const q = query.trim().toLowerCase();
  const visible = products.filter((p) =>
    (!activeCat || p.category === activeCat) &&
    (!q || [p.name, p.sku, p.category].some((v) => (v || "").toLowerCase().includes(q))));
  const gallery = (p: Product) => (p.images && p.images.length ? p.images : p.image_url ? [p.image_url] : []);

  const getEffectivePrice = (p: Product) => p.is_promotional ? p.promotional_price : p.price;
  useEffect(() => {
    localStorage.setItem("mentesa_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: Product) => {
    if (product.stock <= 0) return;
    setCart(prev => {
      const existing = prev.find(c => c.product.id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) return prev;
        return prev.map(c => c.product.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { product, quantity: 1 }];
    });
    toast({ title: `${product.name} adicionado ao carrinho!` });
  };

  const updateQty = (productId: string, delta: number) => {
    setCart(prev => prev.map(c => {
      if (c.product.id !== productId) return c;
      const newQty = c.quantity + delta;
      if (newQty <= 0) return c;
      if (newQty > c.product.stock) return c;
      return { ...c, quantity: newQty };
    }));
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(c => c.product.id !== productId));
  };

  const cartTotal = cart.reduce((sum, c) => sum + getEffectivePrice(c.product) * c.quantity, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const discountPercent = (original: number, promo: number) => {
    if (original <= 0) return 0;
    return Math.round(((original - promo) / original) * 100);
  };

  const handleCheckout = () => {
    if (!user) {
      toast({ title: "Precisa de estar autenticado", description: "Faça login ou crie uma conta para continuar. O seu carrinho será mantido!", variant: "destructive" });
      navigate("/auth?redirect=/loja&checkout=1");
      return;
    }
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleSubmitOrder = async () => {
    if (!clientName || !clientEmail || !clientPhone) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    if (deliveryMethod === "delivery" && !deliveryAddress) {
      toast({ title: "Indique o endereço de entrega", variant: "destructive" });
      return;
    }
    if (!user) return;

    setSubmitting(true);

    // Insert order
    const { data: order, error: orderErr } = await supabase
      .from("orders")
      .insert({
        client_id: user.id,
        client_name: clientName,
        client_email: clientEmail,
        client_phone: clientPhone,
        delivery_method: deliveryMethod,
        delivery_address: deliveryMethod === "delivery" ? deliveryAddress : "",
        notes,
        total_amount: cartTotal,
        status: "pending",
      })
      .select("id")
      .single();

    if (orderErr || !order) {
      toast({ title: "Erro ao criar encomenda", description: orderErr?.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }

    // Insert items
    const items = cart.map(c => ({
      order_id: order.id,
      product_id: c.product.id,
      product_name: c.product.name,
      quantity: c.quantity,
      unit_price: getEffectivePrice(c.product),
    }));

    const { error: itemsErr } = await supabase.from("order_items").insert(items);
    if (itemsErr) {
      toast({ title: "Erro ao guardar itens", variant: "destructive" });
      setSubmitting(false);
      return;
    }

    // Deduzir stock de cada produto
    for (const c of cart) {
      await supabase
        .from("products")
        .update({ stock: Math.max(0, c.product.stock - c.quantity) })
        .eq("id", c.product.id);
    }

    // Atualizar lista de produtos localmente
    setProducts(prev => prev.map(p => {
      const cartItem = cart.find(c => c.product.id === p.id);
      if (cartItem) return { ...p, stock: Math.max(0, p.stock - cartItem.quantity) };
      return p;
    }));

    setCart([]);
    localStorage.removeItem("mentesa_cart");
    setCheckoutOpen(false);
    setSuccessOpen(true);
    setSubmitting(false);
  };

  if (shopEnabled === false) return <Navigate to="/" replace />;
  if (loading || shopEnabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
            <ArrowLeft size={16} /> Voltar ao site
          </Link>

          {/* Cart button */}
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="relative gap-2 rounded-full">
                <ShoppingCart size={18} />
                Carrinho
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="flex flex-col">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <ShoppingCart size={20} /> Carrinho
                </SheetTitle>
              </SheetHeader>

              {cart.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-center">
                  <div>
                    <ShoppingBag size={48} className="mx-auto mb-3 text-muted-foreground/30" />
                    <p className="text-muted-foreground">O seu carrinho está vazio</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-3 mt-4">
                    {cart.map(c => (
                      <div key={c.product.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
                        {c.product.image_url ? (
                          <img src={c.product.image_url} alt={c.product.name} className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center">
                            <Package size={20} className="text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">{c.product.name}</p>
                          <p className="text-sm text-primary font-bold">€{(getEffectivePrice(c.product) * c.quantity).toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateQty(c.product.id, -1)}>
                            <Minus size={14} />
                          </Button>
                          <span className="text-sm font-medium w-6 text-center">{c.quantity}</span>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateQty(c.product.id, 1)}>
                            <Plus size={14} />
                          </Button>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeFromCart(c.product.id)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-4 mt-4 space-y-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-primary">€{cartTotal.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                       O custo de envio será cotado e comunicado antes do pagamento, caso opte por entrega.
                    </p>
                    <Button className="w-full rounded-full gap-2" onClick={handleCheckout}>
                      <ShoppingBag size={16} />
                      Finalizar Encomenda
                    </Button>
                  </div>
                </>
              )}
            </SheetContent>
          </Sheet>
        </div>

        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl font-bold text-foreground mb-3 flex items-center justify-center gap-3">
            <ShoppingBag size={36} className="text-primary" />
            Loja Virtual
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Produtos selecionados para o seu bem-estar. Cuide de si com os nossos artigos exclusivos! 🌿
          </p>
        </div>

        {banners.length > 0 && (
          <div className="relative mb-10 rounded-2xl overflow-hidden aspect-[21/8] bg-muted">
            {banners.map((b, i) => {
              const inner = (
                <>
                  <img src={b.image_url} alt={b.title || "Promoção"} className="w-full h-full object-cover" />
                  {(b.title || b.subtitle) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-foreground/60 to-transparent flex flex-col justify-center p-6 sm:p-10">
                      {b.title && <h2 className="font-display text-2xl sm:text-4xl font-bold text-background">{b.title}</h2>}
                      {b.subtitle && <p className="text-background/90 mt-2 max-w-md">{b.subtitle}</p>}
                    </div>
                  )}
                </>
              );
              return (
                <div key={b.id} className={`absolute inset-0 transition-opacity duration-700 ${i === bannerIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                  {b.link_url ? <a href={b.link_url} className="block w-full h-full">{inner}</a> : inner}
                </div>
              );
            })}
            {banners.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {banners.map((_, i) => (
                  <button key={i} onClick={() => setBannerIdx(i)} aria-label={`Banner ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === bannerIdx ? "w-6 bg-background" : "w-2 bg-background/60"}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {products.length > 0 && (
          <div className="mb-8 space-y-4">
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 rounded-full" placeholder="Pesquisar por nome, SKU ou categoria" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            {categories.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="sm" variant={activeCat === null ? "default" : "outline"} className="rounded-full" onClick={() => setActiveCat(null)}>Todas</Button>
                {categories.map((c) => (
                  <Button key={c} size="sm" variant={activeCat === c ? "default" : "outline"} className="rounded-full" onClick={() => setActiveCat(c)}>{c}</Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products grid */}
        {products.length > 0 && visible.length === 0 && (
          <p className="text-center py-12 text-muted-foreground">Nenhum produto encontrado.</p>
        )}
        {products.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Package size={48} className="mx-auto mb-4 opacity-40" />
            <p>Loja em breve, estamos a preparar os nossos produtos.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p, i) => {
              const inCart = cart.find(c => c.product.id === p.id);
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-2xl bg-card border border-border overflow-hidden group relative"
                >
                  {p.is_promotional && (
                    <div className="absolute top-3 left-3 z-10">
                      <Badge className="bg-destructive text-destructive-foreground gap-1 text-xs shadow-md">
                        <Tag size={12} />
                        -{discountPercent(p.price, p.promotional_price)}%
                      </Badge>
                    </div>
                  )}

                  {gallery(p)[0] ? (
                    <button type="button" onClick={() => { setDetail(p); setMediaIdx(0); }} className="block w-full aspect-square overflow-hidden">
                      <img
                        src={gallery(p)[0]}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ) : (
                    <div className="aspect-square bg-muted flex items-center justify-center">
                      <Package size={48} className="text-muted-foreground/30" />
                    </div>
                  )}

                  <div className="p-5 space-y-3">
                    {p.category && <p className="text-xs uppercase tracking-wide text-primary">{p.category}</p>}
                    <button type="button" onClick={() => { setDetail(p); setMediaIdx(0); }} className="text-left">
                      <h3 className="font-display text-lg font-bold text-foreground hover:text-primary">{p.name}</h3>
                    </button>
                    {p.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-2">
                        {p.is_promotional ? (
                          <>
                            <span className="text-lg font-bold text-primary">€{p.promotional_price.toFixed(2)}</span>
                            <span className="text-sm line-through text-destructive">€{p.price.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-foreground">€{p.price.toFixed(2)}</span>
                        )}
                      </div>

                      {p.stock > 0 ? (
                        inCart ? (
                          <div className="flex items-center gap-1 rounded-full border border-border px-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateQty(p.id, -1)}>
                              <Minus size={14} />
                            </Button>
                            <span className="text-sm font-bold w-6 text-center">{inCart.quantity}</span>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateQty(p.id, 1)}>
                              <Plus size={14} />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" className="rounded-full gap-1" onClick={() => addToCart(p)}>
                            <Plus size={14} /> Adicionar
                          </Button>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground font-medium">Esgotado</span>
                      )}
                    </div>

                    {p.stock <= 3 && p.stock > 0 && (
                      <p className="text-xs text-destructive">Apenas {p.stock} em stock!</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Product detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (() => {
            const media = [...gallery(detail).map((u) => ({ t: "img", u })), ...(detail.videos || []).map((u) => ({ t: "vid", u }))];
            const cur = media[mediaIdx];
            return (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                    {cur?.t === "vid" ? <video src={cur.u} controls className="w-full h-full object-contain bg-foreground" />
                      : cur ? <img src={cur.u} alt={detail.name} className="w-full h-full object-cover" /> : null}
                  </div>
                  {media.length > 1 && (
                    <div className="grid grid-cols-5 gap-2">
                      {media.map((m, i) => (
                        <button key={m.u} onClick={() => setMediaIdx(i)} className={`aspect-square rounded-lg overflow-hidden border-2 ${i === mediaIdx ? "border-primary" : "border-transparent"}`}>
                          {m.t === "vid" ? <video src={m.u} muted className="w-full h-full object-cover" /> : <img src={m.u} alt="" className="w-full h-full object-cover" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {detail.category && <p className="text-xs uppercase tracking-wide text-primary">{detail.category}</p>}
                  <DialogHeader><DialogTitle className="font-display text-2xl">{detail.name}</DialogTitle></DialogHeader>
                  {detail.sku && <p className="text-xs text-muted-foreground">SKU: {detail.sku}</p>}
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-primary">€{getEffectivePrice(detail).toFixed(2)}</span>
                    {detail.is_promotional && <span className="line-through text-destructive">€{detail.price.toFixed(2)}</span>}
                  </div>
                  {detail.description && <p className="text-sm text-muted-foreground whitespace-pre-line">{detail.description}</p>}
                  {detail.stock > 0
                    ? <Button className="rounded-full gap-2 w-full" onClick={() => addToCart(detail)}><Plus size={16} /> Adicionar ao carrinho</Button>
                    : <p className="text-sm text-muted-foreground">Esgotado</p>}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShoppingBag size={20} className="text-primary" />
              Finalizar Encomenda
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Client info */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-foreground">Os seus dados</h3>
              <div className="space-y-2">
                <Label htmlFor="co-name">Nome completo *</Label>
                <Input id="co-name" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="O seu nome" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="co-email">Email *</Label>
                <Input id="co-email" type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="email@exemplo.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="co-phone">Telefone / WhatsApp *</Label>
                <Input id="co-phone" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="+351 912 345 678" />
              </div>
            </div>

            {/* Delivery method */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-foreground">Como pretende receber?</h3>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => setDeliveryMethod("pickup")}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${deliveryMethod === "pickup" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                >
                  <HandHelping size={20} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Retirar em mãos</p>
                    <p className="text-xs text-muted-foreground">Sem custos adicionais, combinamos o local</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryMethod("delivery")}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${deliveryMethod === "delivery" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                >
                  <Truck size={20} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Entrega ao domicílio</p>
                    <p className="text-xs text-muted-foreground">O valor do envio será cotado e confirmado antes do pagamento</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryMethod("massage_pickup")}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${deliveryMethod === "massage_pickup" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                >
                  <Sparkles size={20} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Receber na próxima massagem</p>
                    <p className="text-xs text-muted-foreground">Levamos o produto quando for à sua sessão, sem custo extra!</p>
                  </div>
                </button>
              </div>

              {deliveryMethod === "delivery" && (
                <div className="space-y-2">
                  <Label>Morada de entrega *</Label>
                  <Input value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} placeholder="Rua, número, código postal, cidade" />
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Alguma preferência ou nota especial?" rows={2} />
            </div>

            {/* Summary */}
            <div className="rounded-xl bg-accent/50 border border-border p-4 space-y-2">
              <h3 className="font-medium text-sm">Resumo da encomenda</h3>
              {cart.map(c => (
                <div key={c.product.id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{c.product.name} × {c.quantity}</span>
                  <span className="font-medium">€{(getEffectivePrice(c.product) * c.quantity).toFixed(2)}</span>
                </div>
              ))}
              {deliveryMethod === "delivery" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Envio</span>
                  <span className="text-xs text-primary font-medium">A cotar</span>
                </div>
              )}
              <div className="border-t border-border pt-2 flex justify-between font-bold text-base">
                <span>Total produtos</span>
                <span className="text-primary">€{cartTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Info box */}
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 text-xs text-foreground space-y-1">
              <p className="font-semibold"> Como funciona?</p>
              <p>Após submeter a sua encomenda, entraremos em contacto para confirmar os detalhes, combinar a forma de pagamento{deliveryMethod === "delivery" ? " e o custo de envio" : ""}. O produto será enviado após a confirmação do pagamento.</p>
            </div>

            <Button className="w-full rounded-full gap-2" onClick={handleSubmitOrder} disabled={submitting}>
              {submitting ? "A submeter..." : "Confirmar Encomenda"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog with massage cross-sell */}
      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="max-w-md text-center">
          <div className="space-y-5 py-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ShoppingBag size={32} className="text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold text-foreground">Encomenda recebida! 🎉</h2>
            <p className="text-muted-foreground text-sm">
              Obrigada pela sua compra! Vamos entrar em contacto consigo em breve para confirmar os detalhes e a forma de pagamento.
            </p>

            {/* Cross-sell massage */}
            <div className="rounded-2xl bg-accent/60 border border-border p-5 text-left space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles size={20} className="text-primary" />
                <h3 className="font-display font-bold text-foreground">Que tal uma massagem? </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Complete o seu momento de bem-estar! Agende uma sessão de massagem e receba os seus produtos sem custo de envio, levamos tudo até si.
              </p>
              <Button
                className="w-full rounded-full gap-2"
                onClick={() => {
                  setSuccessOpen(false);
                  navigate("/#agendar");
                }}
              >
                <Sparkles size={14} />
                Agendar Massagem
              </Button>
            </div>

            <Button variant="outline" className="w-full rounded-full" onClick={() => setSuccessOpen(false)}>
              Continuar na loja
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Store;
